/** @forge-header
 * @id          storageGuard
 * @role        Safe localStorage wrappers with schema validation and corruption recovery
 * @pattern     adapter
 * @purity      localStorage
 * @upstream    infra/boundaryLog:boundaryError,boundaryWarn
 * @downstream  kalshiProfiles | themeManager | dataMode | playbook/persistence
 * @exports     safeRead:fn | safeWrite:fn | resetAllLocalStorage:fn
 * @test        storageGuard.test.ts
 */

import { boundaryError, boundaryWarn } from './boundaryLog';

/**
 * Known g1xl localStorage key prefixes and exact keys.
 * Used by resetAllLocalStorage to scope the clear.
 */
const KNOWN_KEY_PREFIXES = [
  'g1xl_',
  'kalshi_',
  'playbook_',
  'app_theme',
  'data_mode',
  'gateRemember',
  'positions_cache',
  'stream_config',
  'layout_mode',
  'defense_',
  'sound_',
  'changelog_',
  'sidebar_',
  'g1mt_tracker_filters', // PositionTracker sport/kind filters (issue #83); scoped
  //                         to this feature's keys so a reset clears them without
  //                         touching auth (g1mt_access_token) or pins.
] as const;

/**
 * Safely read a value from localStorage with JSON parsing and schema validation.
 * Returns `fallback` on any error: missing key, corrupt JSON, schema mismatch.
 */
export function safeRead<T>(key: string, schema: (v: unknown) => v is T, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;

    const parsed: unknown = JSON.parse(raw);
    if (schema(parsed)) return parsed;

    // Schema mismatch — data exists but doesn't match expected shape
    boundaryError('auth', `localStorage schema mismatch for key "${key}"`, {
      type: typeof parsed,
    });
    return fallback;
  } catch (err) {
    // Corrupt JSON or other read error
    boundaryError('auth', `localStorage corruption for key "${key}"`, {
      error: String(err),
    });
    return fallback;
  }
}

/**
 * Safely write a value to localStorage as JSON.
 * Returns true on success, false on quota or serialization errors.
 */
export function safeWrite(key: string, value: unknown): boolean {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (err) {
    boundaryError('auth', `localStorage write failed for key "${key}"`, {
      error: String(err),
    });
    return false;
  }
}

/**
 * Clear all g1xl-related localStorage keys.
 * Scoped to known prefixes to avoid destroying unrelated data.
 */
export function resetAllLocalStorage(): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (KNOWN_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }

  boundaryWarn('auth', `resetAllLocalStorage cleared ${keysToRemove.length} keys`, {
    keys: keysToRemove,
  });
}
